/**
 * @fileoverview The lettering on a share picture: the label line, the
 * gardener's words, the Grow mark's row, the scrim under them, the handle and
 * the signature. Moved out of plant-list/lib/shareFrames (batch 18, A110);
 * nothing in it changed.
 *
 * Where: the layouts and the build (features/share/frames).
 * Settings: SHARE_SIGNATURE, SHARE_TEXT_MAX_LINES (config/tunables/plantList).
 *
 * @module frameText
 */

import type { PlantPhoto } from '@/shared/types/photo';
import { SHARE_SIGNATURE, SHARE_TEXT_MAX_LINES } from '@/config/tunables/plantList';
import { BODY, SERIF, drawGrowMark, drawHandleAt, wrap } from './canvasKit';
import { SHARE_SAFE, TEXT_BASE, metaOf, type ShareFrameInput } from './frameOptions';

/**
 * The signature (SHARE_SIGNATURE): the gardener's Grow address, small, set
 * right-aligned on a baseline, white with a soft shadow over a photograph or
 * in an ink on a plain ground.
 *
 * @param ctx - The frame's canvas.
 * @param text - The address.
 * @param right - Its right edge.
 * @param baseline - Its baseline.
 * @param U - The frame's unit.
 * @param ink - Its colour; white (over a photograph) gets a shadow.
 */
export function drawSignature(ctx: CanvasRenderingContext2D, text: string, right: number, baseline: number, U: number, ink = '#ffffff'): void {
  ctx.save();
  ctx.globalAlpha = SHARE_SIGNATURE.opacity;
  ctx.font = `500 ${Math.round(SHARE_SIGNATURE.sizePx * U)}px ${BODY}`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'alphabetic';
  if (ink === '#ffffff') {
    ctx.shadowColor = 'rgba(0,0,0,0.6)';
    ctx.shadowBlur = Math.round(8 * U);
  }
  ctx.fillStyle = ink;
  ctx.fillText(text, right, baseline);
  ctx.restore();
}

/**
 * Whether this picture of the set carries the signature: the first only
 * (SHARE_SIGNATURE.firstOnly), when there is an address and it is on.
 *
 * @param input - The sharer's choices.
 * @param index - The picture's place in the set.
 */
export function signs(input: ShareFrameInput, index: number): boolean {
  return SHARE_SIGNATURE.show && Boolean(input.address) && (!SHARE_SIGNATURE.firstOnly || index === 0);
}

/** One piece of the label line, in reading order, already filtered. */
export interface MetaPart {
  /** What the piece says. */
  text: string;
  /** Set in the serif and the strong colour: the plant's name. */
  serif?: boolean;
  /** A coloured dot before it, once the stage's; nothing sets one now. */
  dot?: string;
}

/**
 * The label line's pieces for a photograph: name, variety, stage and date,
 * each only when the gardener prints it and it has something to say.
 *
 * @param input - The sharer's choices.
 * @param photo - The photograph the line is under.
 */
function metaParts(input: ShareFrameInput, photo: PlantPhoto): MetaPart[] {
  const parts: MetaPart[] = [];
  const meta = metaOf(input, photo);
  const { elements } = meta;
  if (elements.name && meta.name) parts.push({ text: meta.name, serif: true });
  if (elements.variety && meta.variety && meta.variety !== meta.name) {
    parts.push({ text: meta.variety });
  }
  // No stage: a photo has none (2026-10-03). A plant's stage is its history, not a photo's; printing its
  // stage on the photo's day could come back (PlantStageLine, plantStage), and saved styles keep the switch.
  // In before-and-after the two dates are in the band across the seam, so
  // repeating one down here would say the same thing twice and less clearly.
  if (elements.date && photo.date_taken && input.layout !== 'ba') {
    parts.push({ text: input.formatDate(photo.date_taken) });
  }
  // A location's share: where a plant's photo grows, how many grow there.
  if (elements.place && meta.place) parts.push({ text: meta.place });
  if (elements.count && meta.count) parts.push({ text: meta.count });
  return parts;
}

/** The colours the text block is set in: white over a photograph, ink on a card. */
export interface TextTheme {
  /** The gardener's words and the plant's name. */
  strong: string;
  /** The rest of the label line. */
  soft: string;
  /** The Grow mark. */
  source: string;
  /** Whether the block needs a darkening behind it. */
  scrim: boolean;
}
/** Lettering over a photograph. */
const OVER_PHOTO: TextTheme = { strong: 'rgba(255,255,255,0.97)', soft: 'rgba(255,255,255,0.86)', source: 'rgba(255,255,255,0.92)', scrim: true };
/** Lettering on the cream card. */
export const ON_CARD: TextTheme = { strong: '#3D3935', soft: 'rgba(61,57,53,0.78)', source: '#2D5A27', scrim: false };

/** What the text block will need, measured before anything is drawn. */
export interface TextPlan {
  /** The label line's pieces. */
  parts: MetaPart[];
  /** The label line, wrapped. */
  metaLines: string[];
  /** The gardener's words, wrapped. */
  wordLines: string[];
  /** The label line's font. */
  metaFont: string;
  /** The words' font. */
  wordsFont: string;
  /** From one label line's baseline to the next. */
  metaLead: number;
  /** From one line of words' baseline to the next. */
  wordsLead: number;
  /** The Grow mark's row: none on an account without it. */
  sourceH: number;
  /** From the first line's top to the last line's baseline. */
  height: number;
}

/**
 * Measures the text block before it is drawn, so a layout can make room for it.
 *
 * @param ctx - The frame's canvas, for measuring.
 * @param input - The sharer's choices.
 * @param photo - The photograph the block is for.
 * @param inner - The width the lines may take.
 * @param U - The lettering's unit: the frame's, times the chosen text size.
 */
export function planText(ctx: CanvasRenderingContext2D, input: ShareFrameInput, photo: PlantPhoto, inner: number, U: number): TextPlan {
  // Sized for a phone, where a 1080px frame is seen at a third of its size:
  // the label line at 30px read as small print nobody could make out.
  const metaFont = `400 ${Math.round(40 * U)}px ${BODY}`;
  const wordsFont = `400 ${Math.round(60 * U)}px ${BODY}`;
  const metaLead = Math.round(54 * U);
  const wordsLead = Math.round(74 * U);
  // No room for the mark on an account without it.
  const sourceH = input.showMark === false ? 0 : Math.round(54 * U);

  const parts = metaParts(input, photo);
  ctx.font = metaFont;
  // Three lines, so the label is not cut short at the larger text sizes.
  const metaLines = parts.length ? wrap(ctx, parts.map((p) => p.text).join('  \u00b7  '), inner, SHARE_TEXT_MAX_LINES) : [];
  ctx.font = wordsFont;
  const words = input.elements.words ? input.wordsFor(photo).trim() : '';
  const wordLines = words ? wrap(ctx, words, inner, SHARE_TEXT_MAX_LINES) : [];

  let height = sourceH;
  if (wordLines.length) height += Math.round(66 * U) + wordsLead * (wordLines.length - 1);
  if (metaLines.length) height += Math.round(58 * U) + metaLead * (metaLines.length - 1);
  return { parts, metaLines, wordLines, metaFont, wordsFont, metaLead, wordsLead, sourceH, height };
}

/**
 * The text block, in the order a shared page keeps: where it came from, then
 * the line that is the point, then the small print.
 *
 * The source row is the first line rather than a corner. A mark beside the
 * word Grow, the way a shared page shows its favicon and domain, so it reads
 * as a source and not as a watermark. Then the gardener's own words, the
 * largest text on the frame, and last the label line: name, variety, stage,
 * date, at label size. The photograph is the draw; the lettering keeps out of
 * its way.
 *
 * Drawn from a top edge downwards, so the same block sits at the foot of a
 * photograph or inside a card.
 *
 * @param ctx - The frame's canvas.
 * @param plan - The block, measured (planText).
 * @param x - The block's left edge.
 * @param top - The block's top edge.
 * @param U - The lettering's unit.
 * @param theme - The colours to set it in.
 */
export function drawTextBlock(
  ctx: CanvasRenderingContext2D, plan: TextPlan, x: number, top: number, U: number, theme: TextTheme
): void {
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';

  // The source row: leaf, then the word. None where the plan left no room for it (an account without the mark).
  if (plan.sourceH > 0) drawGrowMark(ctx, x, top + Math.round(38 * U), U, theme.source);

  let cursor = top + plan.sourceH;

  if (plan.wordLines.length) {
    cursor += Math.round(66 * U);
    ctx.font = plan.wordsFont;
    ctx.fillStyle = theme.strong;
    plan.wordLines.forEach((line, index) => {
      ctx.fillText(line, x, cursor + plan.wordsLead * index);
    });
    cursor += plan.wordsLead * (plan.wordLines.length - 1);
  }

  if (plan.metaLines.length) {
    cursor += Math.round(58 * U);
    if (plan.metaLines.length === 1) {
      drawMetaRun(ctx, plan.parts, x, cursor, plan.metaFont, U, theme);
    } else {
      ctx.font = plan.metaFont;
      ctx.fillStyle = theme.soft;
      plan.metaLines.forEach((line, index) => {
        ctx.fillText(line, x, cursor + plan.metaLead * index);
      });
    }
  }
}

/**
 * The lettering at the foot of a photograph: the block, and the scrim that
 * keeps it legible, sized to what the block needs and no more.
 *
 * @param ctx - The frame's canvas.
 * @param input - The sharer's choices.
 * @param photo - The photograph the lettering is for.
 * @param W - The frame's width.
 * @param H - The frame's height.
 * @param U - The frame's unit (its width over 1080).
 * @param signed - This picture carries the signature (the set's first).
 */
export function drawText(
  ctx: CanvasRenderingContext2D, input: ShareFrameInput, photo: PlantPhoto,
  W: number, H: number, U: number, signed = false
): void {
  const margin = Math.round(64 * U);
  const inner = W - margin * 2;
  const safeTop = Math.round(H * SHARE_SAFE[input.size].top);
  const safeBottom = Math.round(H * SHARE_SAFE[input.size].bottom);

  // Lettering is measured in T, the frame's unit times the chosen text size;
  // margins and the scrim stay in U, the frame's own.
  const T = U * TEXT_BASE * (input.textScale ?? 1);
  const plan = planText(ctx, input, photo, inner, T);
  // The signature's line under the block, when this picture carries it.
  const signLine = signed ? Math.round((SHARE_SIGNATURE.sizePx + 22) * U) : 0;
  const bottom = H - safeBottom - margin - signLine;
  const top = bottom - plan.height;

  // Darkens fully by the last line rather than by the bottom edge, or on a
  // story the words would sit in the thin half of a gradient aimed at a
  // corner they no longer reach.
  const scrimTop = Math.min(H * 0.62, top - Math.round(90 * U));
  const full = Math.min(1, (bottom + Math.round(24 * U) - scrimTop) / (H - scrimTop));
  const gradient = ctx.createLinearGradient(0, scrimTop, 0, H);
  gradient.addColorStop(0, 'rgba(16,20,14,0)');
  gradient.addColorStop(Math.max(0.01, full * 0.45), 'rgba(16,20,14,0.34)');
  gradient.addColorStop(full, 'rgba(16,20,14,0.82)');
  gradient.addColorStop(1, 'rgba(16,20,14,0.82)');
  ctx.fillStyle = gradient;
  // Nothing printed: no shade at all, or with the signature only a whisper of it under the address.
  if (plan.height > 0) ctx.fillRect(0, scrimTop, W, H - scrimTop);
  else if (signed) {
    const low = ctx.createLinearGradient(0, H - Math.round(160 * U), 0, H);
    low.addColorStop(0, 'rgba(16,20,14,0)');
    low.addColorStop(1, 'rgba(16,20,14,0.45)');
    ctx.fillStyle = low;
    ctx.fillRect(0, H - Math.round(160 * U), W, Math.round(160 * U));
  }

  if (plan.height > 0) drawTextBlock(ctx, plan, margin, top, T, OVER_PHOTO);
  drawHandle(ctx, input, margin, safeTop, T, undefined, photo);
  if (signed) {
    const inset = Math.round(SHARE_SIGNATURE.insetPx * U);
    drawSignature(ctx, input.address as string, W - inset, H - safeBottom - inset, U);
  }
}

/**
 * Their handle, top left, in the reading position, with the mark of the
 * place it lives in that place's colour. Theirs outranks ours, and a bare
 * "@name" says nothing about where to look for it.
 *
 * @param ctx - The frame's canvas.
 * @param input - The sharer's choices, which say whether and what to print.
 * @param margin - The frame's margin.
 * @param safeTop - What the app showing a story covers at its top.
 * @param U - The lettering's unit.
 * @param ink - The handle's colour: white over a photograph, the ground's ink on a card.
 * @param photo - The photograph the frame is of, for its own switch in a location's share.
 */
export function drawHandle(
  ctx: CanvasRenderingContext2D, input: ShareFrameInput, margin: number, safeTop: number, U: number,
  ink = 'rgba(255,255,255,0.95)',
  photo?: PlantPhoto,
): void {
  // The photograph's own switch, in a location's share.
  const elements = photo ? metaOf(input, photo).elements : input.elements;
  if (!(elements.handle && input.handle)) return;
  drawHandleAt(ctx, input.handle, margin, safeTop, U, ink);
}

/**
 * The label line on one row: the plant's name in the serif, the rest soft,
 * with a dot between the pieces (and a coloured dot before one that has it).
 *
 * @param ctx - The frame's canvas.
 * @param parts - The pieces, in order.
 * @param x - The line's left edge.
 * @param baseline - Its baseline.
 * @param font - The label's font; the name takes its serif twin.
 * @param U - The lettering's unit.
 * @param theme - The colours to set it in.
 */
function drawMetaRun(
  ctx: CanvasRenderingContext2D, parts: MetaPart[],
  x: number, baseline: number, font: string, U: number, theme: TextTheme
): void {
  ctx.font = font;
  const serifFont = font.replace('400', '600').replace(BODY, SERIF);
  const gap = Math.round(10 * U);
  const dotR = Math.round(5 * U);
  let cursor = x;

  parts.forEach((part, index) => {
    if (index > 0) {
      ctx.font = font;
      ctx.fillStyle = theme.soft;
      ctx.globalAlpha = 0.55;
      const sep = '\u00b7';
      ctx.fillText(sep, cursor + gap, baseline);
      ctx.globalAlpha = 1;
      cursor += gap * 2 + ctx.measureText(sep).width;
    }
    if (part.dot) {
      ctx.beginPath();
      ctx.arc(cursor + dotR, baseline - dotR * 1.2, dotR, 0, Math.PI * 2);
      ctx.fillStyle = part.dot;
      ctx.fill();
      cursor += dotR * 2 + Math.round(8 * U);
    }
    ctx.font = part.serif ? serifFont : font;
    ctx.fillStyle = part.serif ? theme.strong : theme.soft;
    ctx.fillText(part.text, cursor, baseline);
    cursor += ctx.measureText(part.text).width;
  });
}
